import { Box, Container, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { useLoad } from "@redotech/react-util/load";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnableItem } from "@redotech/redo-model/draft-return/draft-return-items";
import { getPrimaryProduct } from "@redotech/redo-model/draft-return/util";
import { ExchangeGroup } from "@redotech/redo-model/team";
import { Flex } from "@redotech/redo-web/flex";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import {
  getProductsByCollection,
  getProductsByTags,
  ShopifyStorefrontClient,
  ProductInfo as StorefrontProductInfo,
} from "@redotech/shopify-storefront";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/util/shopify-id";
import { memo, useState } from "react";
import {
  exchangeGroupFilter,
  excludeVariantExchangeStoreUrls,
  getProductInfo,
} from "../../util/util";
import * as advancedExchange from "./advanced-exchange.module.css";
import { ExchangeItem } from "./exchange-item";
import { ItemDetailsModal } from "./item-details-modal";
import { SearchBar } from "./search-bar";
import {
  ExchangeProduct,
  ExchangeProductVariant,
  isVariantInStock,
} from "./variant-bubbles";

const filterProducts = (
  product: ExchangeProduct,
  settings: ReturnAppSettings,
) => {
  const excludedProductTags = new Set(
    settings?.exchanges?.excludeProductTags.map((tag: string) =>
      tag.toLowerCase(),
    ),
  );

  return (
    product.publishedAt !== null &&
    product.variants.some(
      (variant) =>
        // Hide virtual products
        variant.requiresShipping && isVariantInStock(variant, settings),
    ) &&
    // Hide products with excluded tags
    !product.tags.some((tag: string) =>
      excludedProductTags.has(tag.toLowerCase()),
    )
  );
};

export const ExchangeGroupExchange = memo(function ExchangeGroupExchange({
  exchangeGroup,
  onItemSelected,
  settings,
  selectedReturnItem,
}: {
  exchangeGroup: ExchangeGroup;
  onItemSelected: (variant: ExchangeProductVariant | null) => void;
  settings: ReturnAppSettings;
  selectedReturnItem: ReturnableItem;
}) {
  const [itemDetailsModalOpen, setItemDetailsModalOpen] =
    useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] =
    useState<ExchangeProduct | null>(null);
  const [displayingSearchResults, setDisplayingSearchResults] = useState(false);
  const [searchedItems, setSearchedItems] = useState<ExchangeProduct[]>([]);
  const [searchString, setSearchString] = useState<string>(
    excludeVariantExchangeStoreUrls.includes(settings.storeUrl)
      ? (getPrimaryProduct(selectedReturnItem)?.title ?? "")
      : "",
  );
  const [searchQuery, setSearchQuery] = useState<string>(searchString);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const productsLoad = useLoad(async () => {
    let products: StorefrontProductInfo[] = [];
    const storefrontClient = new ShopifyStorefrontClient(
      settings.storeUrl,
      settings.storefrontAccessToken,
    );

    switch (exchangeGroup.type) {
      case "name:collection":
      case "tag:collection":
      case "collection:collection":
        products = await getProductsByCollection(
          storefrontClient,
          convertShopifyIdToGid(
            exchangeGroup.targetCollection.id,
            ShopifyIdType.Collection,
          ),
          100,
        );
        break;
      case "tag:tag":
        products = await getProductsByTags(
          storefrontClient,
          exchangeGroup.targetTags,
        );
        break;
    }
    return products.map((product) => getProductInfo(product));
  }, [exchangeGroup, settings.storefrontAccessToken, settings.storeUrl]);

  const clearSearchResults = () => {
    setDisplayingSearchResults(false);
    setSearchedItems([]);
  };

  const excludedProductTags = settings?.exchanges?.excludeProductTags.map(
    (tag: string) => tag.toLowerCase(),
  );
  const filterSearchProduct = (product: ExchangeProduct): boolean => {
    return (
      exchangeGroupFilter(product, exchangeGroup) &&
      product.availableForSale &&
      product.vendor !== "re:do" &&
      !product.tags.some((tag: string) =>
        excludedProductTags.includes(tag.toLowerCase()),
      )
    );
  };

  if (productsLoad?.pending) {
    return (
      <div className={advancedExchange.noProductsTextContainer}>
        <Typography variant="h5">Loading...</Typography>
      </div>
    );
  } else if (productsLoad?.error) {
    return (
      <div className={advancedExchange.noProductsTextContainer}>
        <Typography variant="h5">No products available</Typography>
      </div>
    );
  }

  const filteredProducts = (productsLoad?.value || []).filter((product) =>
    filterProducts(product, settings),
  );

  if (filteredProducts.length === 0) {
    return (
      <div className={advancedExchange.noProductsTextContainer}>
        <Typography variant="h5">No products available</Typography>
      </div>
    );
  }

  if (itemDetailsModalOpen && selectedProduct?.variants) {
    return (
      <ItemDetailsModal
        exchangeGroup={exchangeGroup}
        onItemSelected={onItemSelected}
        open={itemDetailsModalOpen}
        selectedProduct={selectedProduct}
        setOpen={setItemDetailsModalOpen}
        settings={settings}
      />
    );
  }

  return (
    <Flex align="stretch" className={advancedExchange.fullWidth} dir="column">
      <Flex dir="row" justify="space-between" reverse>
        <SearchBar
          filterSearchProduct={filterSearchProduct}
          getProductInfo={getProductInfo}
          searchQuery={searchQuery}
          setDisplayingSearchResults={setDisplayingSearchResults}
          setSearchedItems={setSearchedItems}
          setSearchLoading={setSearchLoading}
          setSearchQuery={setSearchQuery}
          setSearchString={setSearchString}
          settings={settings}
        />

        {displayingSearchResults && (
          <Flex dir="column" justify="flex-start">
            <Flex dir="row">
              <Typography variant="h6">
                You searched for "{searchString}"
              </Typography>
              <Chip
                className={advancedExchange.exitButton}
                label=""
                onDelete={clearSearchResults}
              />
            </Flex>
            <p className={advancedExchange.resultsText}>
              {searchedItems.length} Results
            </p>
          </Flex>
        )}
      </Flex>

      <Container maxWidth="lg" sx={{ display: "inherit" }}>
        <Grid container spacing={0}>
          <Box
            className={advancedExchange.circularProgressContainer}
            sx={{
              display: productsLoad.pending ? "inherit" : "none!important",
            }}
          >
            <div className={advancedExchange.spinnerContainer}>
              <Spinner style={SpinnerStyle.DARK} />
            </div>
          </Box>
          {!displayingSearchResults &&
            filteredProducts.map((product) => (
              <Grid
                className={advancedExchange.exchangeItemGrid}
                item
                key={product.id}
                lg={2.4}
                md={3}
                sm={4}
                xs={6}
              >
                <ExchangeItem
                  exchangeGroup={exchangeGroup}
                  product={product}
                  setItemDetailsModalOpen={setItemDetailsModalOpen}
                  setSelectedProduct={setSelectedProduct}
                />
              </Grid>
            ))}
          {!searchLoading &&
            searchedItems.length === 0 &&
            searchString !== "" && (
              <Typography
                className={advancedExchange.noResultsText}
                variant="h5"
              >
                No results found
              </Typography>
            )}
          {displayingSearchResults &&
            searchedItems.map((product) => (
              <Grid
                className={advancedExchange.exchangeItemGrid}
                item
                key={product.id}
                lg={2.4}
                md={3}
                sm={4}
                xs={6}
              >
                <ExchangeItem
                  exchangeGroup={exchangeGroup}
                  product={product}
                  setItemDetailsModalOpen={setItemDetailsModalOpen}
                  setSelectedProduct={setSelectedProduct}
                />
              </Grid>
            ))}
        </Grid>
      </Container>
    </Flex>
  );
});
